import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Alert, Spinner, Card, Badge } from 'react-bootstrap';
import { useParams } from "react-router-dom";
import { getItemByToken } from '../../services/Items/Inventory';
import QRGenerator from '../../components/Common/QRGenerator';
import './ViewItemByToken.css';

function ViewItemByToken() {
  const { token } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [itemData, setItemData] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertText, setAlertText] = useState('');
  const hasCheckedToken = useRef(false);

  useEffect(() => {

    if (hasCheckedToken.current) {
      console.log('⚠️ Ya se verificó el token, saliendo...');
      return;
    }

    if (!token) {
      console.log('❌ No hay token en la URL');
      setIsLoading(false);
      setIsValidToken(false);
      setShowAlert(true);
      setAlertText('No se proporcionó un token válido para visualizar el objeto.');
      hasCheckedToken.current = true;
      return;
    }

    hasCheckedToken.current = true;    

    getItemByToken(token).then((response) => {
      console.log("response.data", response.data)
      if (response.result && response.data) {        
        setIsValidToken(true);
        setItemData(response.data);
      } else {        
        setIsValidToken(false);
        setShowAlert(true);
        setAlertText('El token proporcionado no es válido o el objeto no existe.');
      }
    }).catch((error) => {      
      setIsValidToken(false);
      setShowAlert(true);
      setAlertText('Ocurrió un error al obtener la información del objeto.');
    }).finally(() => {      
      setIsLoading(false);
    });
  }, [token]);

  const isValidValue = (value) => {
    return value !== null && value !== undefined && value !== '' && value !== 0;
  };

  const formatCurrency = (value) => {
    // Verificar si el valor es válido
    if (value === null || value === undefined || value === '' || isNaN(value)) {
      return 'N/A';
    }
    
    // Convertir a número y formatear
    const number = parseFloat(value);
    
    // Verificar si la conversión fue exitosa
    if (isNaN(number)) {
      return 'N/A';
    }
    
    // Formatear con comas y 2 decimales
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number);
  };

  const calculateInventoryValue = () => {
    const quantity = itemData?.quantity;
    const unitPrice = itemData?.unitPrice;
    
    // Verificar si ambos valores son válidos
    const isQuantityValid = quantity !== null && quantity !== undefined && quantity !== '' && !isNaN(quantity);
    const isUnitPriceValid = unitPrice !== null && unitPrice !== undefined && unitPrice !== '' && !isNaN(unitPrice);
    
    // Si quantity es válido y unitPrice es válido, multiplicar
    if (isQuantityValid && isUnitPriceValid) {
      return parseFloat(quantity) * parseFloat(unitPrice);
    }
    
    // Si quantity no es válido pero unitPrice sí, retornar unitPrice
    if (!isQuantityValid && isUnitPriceValid) {
      return parseFloat(unitPrice);
    }
    
    // Si ambos no son válidos, retornar null
    return null;
  };

  const renderFilePreview = (file) => {
    const fileName = file.name || file.file_name || '';
    const fileExtension = fileName.split('.').pop().toLowerCase();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
    
    const isImage = imageExtensions.includes(fileExtension) || 
                    (file.mime_type && file.mime_type.startsWith('image/'));
    const isPdf = fileExtension === 'pdf' || file.mime_type === 'application/pdf';
    
    if (isImage) {
      return (
        <div className="file-card">
          <div className="file-image-container">
            <img 
              src={file.url} 
              alt={fileName}
              className="file-image"
            />
          </div>
          <div className="file-info">
            <div className="file-name">{fileName}</div>
            <div className="file-size">
              {(parseFloat(file.size) / 1024).toFixed(2)} KB
            </div>
          </div>
        </div>
      );
    } else if (isPdf) {
      return (
        <div className="file-card">
          <div className="file-pdf-container">
            <div className="file-pdf-icon">
              <i className="bi bi-file-pdf"></i>
            </div>
            <iframe
              src={file.url}
              className="file-pdf-iframe"
              title={fileName}
            />
          </div>
          <div className="file-info">
            <div className="file-name">{fileName}</div>
            <div className="file-size">
              {(parseFloat(file.size) / 1024).toFixed(2)} KB
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="file-card">
          <div className="file-download-card">
            <div className="file-download-icon">
              <i className="bi bi-file-earmark"></i>
            </div>
            <div className="file-download-info">
              <div className="file-name">{fileName}</div>
              <div className="file-size">
                {(parseFloat(file.size) / 1024).toFixed(2)} KB
              </div>
              <a 
                href={file.url} 
                download={fileName}
                className="btn btn-primary btn-sm file-download-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="bi bi-download"></i> Descargar
              </a>
            </div>
          </div>
        </div>
      );
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-card">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3 mb-0" style={{ color: '#2d3748', fontWeight: 600 }}>
            Cargando información...
          </p>
        </div>
      </div>
    );
  }

  if (!isValidToken) {
    return (
      <div className="error-container">
        <div className="error-card">
          <i className="bi bi-exclamation-triangle error-icon"></i>
          <h2>Token no válido</h2>
          <Alert variant="warning" show={showAlert} className="mb-0">
            {alertText}
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="view-item-container">
      {/* Header */}
      <div className="view-item-header">
        <div className="text-center">
          <i className="bi bi-box-seam view-item-header-icon"></i>
          <h1>{itemData.name || 'Información'}</h1>
        </div>
      </div>

      {/* Contenido */}
      <div className="view-item-content">
        {/* Toda la información en una sola tabla */}
        <div className="info-card">
          <div className="info-card-header">
            Información
          </div>
          <div className="info-card-body">
            {isValidValue(itemData.description) && (
              <div className="info-row">
                <div className="info-label">Descripción</div>
                <div className="info-value">{itemData.description}</div>
              </div>
            )}

            {isValidValue(itemData.categoryName) && (
              <div className="info-row">
                <div className="info-label">Categoría</div>
                <div className="info-value">{itemData.categoryName}</div>
              </div>
            )}

            {isValidValue(itemData.subcategoryName) && (
              <div className="info-row">
                <div className="info-label">Subcategoría</div>
                <div className="info-value">{itemData.subcategoryName}</div>
              </div>
            )}

            {isValidValue(itemData.quantity) && (
              <div className="info-row">
                <div className="info-label">Cantidad</div>
                <div className="info-value">{itemData.quantity}</div>
              </div>
            )}

            {isValidValue(itemData.unitPrice) && (
              <div className="info-row">
                <div className="info-label">Costo Unitario</div>
                <div className="info-value">{formatCurrency(itemData.unitPrice)}</div>
              </div>
            )}

            {(() => {
              const inventoryValue = calculateInventoryValue();
              return inventoryValue !== null && (
                <div className="info-row">
                  <div className="info-label">Valor en Inventario</div>
                  <div className="info-value">{formatCurrency(inventoryValue)}</div>
                </div>
              );
            })()}

            {isValidValue(itemData.locationName) && (
              <div className="info-row">
                <div className="info-label">Ubicación</div>
                <div className="info-value">{itemData.locationName}</div>
              </div>
            )}

            {isValidValue(itemData.statusName) && (
              <div className="info-row">
                <div className="info-label">Estatus</div>
                <div className="info-value">{itemData.statusName}</div>
              </div>
            )}

            {isValidValue(itemData.assignedToUser) && (
              <div className="info-row">
                <div className="info-label">Asignado a</div>
                <div className="info-value">{itemData.assignedToUser}</div>
              </div>
            )}

            {isValidValue(itemData.barcode) && (
              <div className="info-row">
                <div className="info-label">Código de Barras</div>
                <div className="info-value">{itemData.barcode}</div>
              </div>
            )}

            {isValidValue(itemData.serialNumber) && (
              <div className="info-row">
                <div className="info-label">Serial Number</div>
                <div className="info-value">{itemData.serialNumber}</div>
              </div>
            )}

            {isValidValue(itemData.partNumber) && (
              <div className="info-row">
                <div className="info-label">Part Number</div>
                <div className="info-value">{itemData.partNumber}</div>
              </div>
            )}

            {isValidValue(itemData.tag) && (
              <div className="info-row">
                <div className="info-label">Etiqueta</div>
                <div className="info-value">{itemData.tag}</div>
              </div>
            )}

            {isValidValue(itemData.brand) && (
              <div className="info-row">
                <div className="info-label">Marca</div>
                <div className="info-value">{itemData.brand}</div>
              </div>
            )}

            {isValidValue(itemData.model) && (
              <div className="info-row">
                <div className="info-label">Modelo</div>
                <div className="info-value">{itemData.model}</div>
              </div>
            )}

            {isValidValue(itemData.dimensions) && (
              <div className="info-row">
                <div className="info-label">Dimensiones</div>
                <div className="info-value">{itemData.dimensions}</div>
              </div>
            )}

            {isValidValue(itemData.weight) && (
              <div className="info-row">
                <div className="info-label">Peso</div>
                <div className="info-value">{itemData.weight} {itemData.unitOfWeightMeasurement}</div>
              </div>
            )}

            {isValidValue(itemData.acquisitionDateHumanFormatted) && (
              <div className="info-row">
                <div className="info-label">Fecha de Adquisición</div>
                <div className="info-value">{itemData.acquisitionDateHumanFormatted}</div>
              </div>
            )}

            {isValidValue(itemData.additionalNotes) && (
              <div className="info-row">
                <div className="info-label">Notas Adicionales</div>
                <div className="info-value">{itemData.additionalNotes}</div>
              </div>
            )}
          </div>
        </div>

        {/* Archivos adjuntos */}
        {itemData.files && itemData.files.length > 0 && (
          <div className="files-section">
            <div className="info-card">
              <div className="info-card-header">
                Archivos Adjuntos ({itemData.files.length})
              </div>
            </div>
            {itemData.files.map((file) => (
              <div key={file.id}>
                {renderFilePreview(file)}
              </div>
            ))}
          </div>
        )}

        {/* Sección de descarga de QR */}
        <div className="info-card pb-5 mb-5">
          <div className="info-card-header">
            Código QR
          </div>
          <div className="info-card-body text-center pb-5 mb-5">
            <p className="info-value mb-3">
              Para descargar el código QR, haz clic en el siguiente botón
            </p>
            <QRGenerator
              url={`${process.env.REACT_APP_BASE_URL}/viewItem/${token}`}
              filename={`qr-${itemData.name || 'item'}-${itemData.id}`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ViewItemByToken;
